import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Loader2, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SchoolSearch - opens an "every college" dropdown on focus and filters
 * as you type. The dropdown is always rooted in the local server-side
 * index of US colleges (no upstream API calls per keystroke), so the
 * full list shows up instantly. Empty query shows the top 12 schools
 * by enrollment so the user can browse without typing.
 */
export default function SchoolSearch({ onSelect, autoFocus = true, placeholder = "Search any US college, e.g. Vanderbilt" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [indexSize, setIndexSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Browse-list seed: a couple of common letters cover most schools.
  // We fire one fetch per query change with 200ms debounce.
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const query = q.trim() || "univ"; // empty box shows a useful default list
        const r = await fetch(`${API}/netprice/search?q=${encodeURIComponent(query)}`);
        const j = await r.json();
        setResults(j.results || []);
        setIndexSize(j.indexSize || 0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  // Outside click closes
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const empty = !loading && results.length === 0;
  const hint = useMemo(() => {
    if (!indexSize) return "";
    return `${indexSize} accredited US colleges in the index`;
  }, [indexSize]);

  return (
    <div ref={ref} className="relative w-full" data-testid="netprice-school-search">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#9ca3af" }} />
        <input
          autoFocus={autoFocus}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full h-12 pl-10 pr-10 rounded-md text-sm outline-none transition-colors"
          style={{ background: "#ffffff", border: "1px solid #e5e7eb", color: "#111928" }}
          data-testid="netprice-school-input"
        />
        {loading
          ? <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" style={{ color: "#9ca3af" }} />
          : <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "#9ca3af" }} />
        }
      </div>
      {hint && (
        <p className="text-[11px] mt-1.5" style={{ color: "#9ca3af" }} data-testid="netprice-index-hint">
          {hint}. Don't see yours? Type its name and we'll match it.
        </p>
      )}

      {open && (
        <ul
          className="absolute left-0 right-0 mt-1 rounded-md shadow-lg z-30 fade-in max-h-[28rem] overflow-y-auto"
          style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}
          data-testid="netprice-school-list"
          role="listbox">
          {empty ? (
            <li
              className="px-4 py-3 text-sm"
              style={{ color: "#6b7280" }}
              data-testid="netprice-school-empty">
              No schools matched "{q}". Try a different name or a common nickname.
            </li>
          ) : results.map(s => (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => { onSelect(s); setQ(s.name); setOpen(false); }}
                className="w-full text-left px-4 py-3 text-sm hover:bg-blue-50 transition-colors"
                data-testid={`netprice-school-result-${s.id}`}
                style={{ borderBottom: "1px solid #f3f4f6" }}>
                <div style={{ color: "#111928", fontWeight: 500 }}>{s.name}</div>
                <div className="text-xs mt-0.5" style={{ color: "#6b7280" }}>
                  {[s.city, s.state, s.type, s.size ? `${s.size.toLocaleString()} students` : null]
                    .filter(Boolean).join(" - ")}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
